import {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {TablerPlus, TablerLock, TablerBolt} from '../../icons';
import usePlannerStyles from '../planner/usePlannerStyles';
import {RECAP_PRIMARY_BUTTON_PROPS} from '../common/recapButtonStyles';
import type {TelephoneEncounterAction} from '../../types/telephoneEncounter';
import TeDictationButton from './TeDictationButton';
import {TE_GENERATED_REVEAL_SX} from './telephoneEncounterUi';
import DictationEditFlash from '../common/DictationEditFlash';

// Matches the note-preview voice-edit flash lifetime — the shine self-fades
// ~1.7s in; we unmount shortly after so a follow-up edit replays it.
const VOICE_EDIT_FLASH_MS = 3500;

// Generated drafts routinely run several lines (summary paragraph + the
// "Opalite ID" trailer); past this the composer stops growing and scrolls.
const COMPOSER_MAX_HEIGHT_PX = 360;

function formatActionTime(value: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (sameDay) return `Today, ${time}`;
  return `${date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}, ${time}`;
}

// The repeatable "Action Taken" thread. Any contributor can append an entry
// (spoke to pt, left message, gave instruction…) and it bounces between staff
// until someone closes the encounter, at which point the thread locks.
const ActionTakenThread = ({
  actions,
  onAdd,
  onGenerate,
  onVoiceEdit,
  autoGenerate = false,
  isLocked = false,
  isAdding = false,
}: {
  actions: TelephoneEncounterAction[];
  // Resolves to `false` when the add failed (e.g. the EHR write was rejected),
  // so the composer can keep the draft text for a retry instead of clearing it.
  onAdd: (text: string) => void | boolean | Promise<void | boolean>;
  // Optional: generate an action-taken suggestion from the call transcript;
  // resolves to the suggested text, which prefills the composer for review.
  onGenerate?: () => Promise<string>;
  // Optional: apply a spoken voice-edit instruction to the current composer
  // draft via the backend LLM, resolving to the full revised text (or `null`
  // when it failed and the draft should be left untouched). When omitted, the
  // Voice edit button falls back to appending the raw transcript.
  onVoiceEdit?: (
    currentText: string,
    instruction: string,
  ) => Promise<string | null>;
  // When true, run `onGenerate` once on mount (for an empty, unlocked thread)
  // so the action note is auto-drafted from the call instead of starting blank.
  autoGenerate?: boolean;
  isLocked?: boolean;
  isAdding?: boolean;
}) => {
  const {borderColor, inputFocusStyles, subtleText, textColor, panelBg} =
    usePlannerStyles();
  const [draft, setDraft] = useState('');
  // Mirror of `draft` so the async voice-edit handler reads the latest text
  // even though it closes over the draft at the time dictation started.
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  // Grow the composer with its content (generated drafts arrive several lines
  // tall); rows={3} stays the floor, COMPOSER_MAX_HEIGHT_PX the ceiling.
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const fitted = Math.min(el.scrollHeight + 2, COMPOSER_MAX_HEIGHT_PX);
    el.style.height = `${fitted}px`;
    el.style.overflowY =
      el.scrollHeight + 2 > COMPOSER_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, [draft]);
  const [generating, setGenerating] = useState(false);
  // Plays the one-shot reveal on the composer the moment a generated draft
  // lands, then clears on animation end so manual edits don't replay it.
  const [draftJustGenerated, setDraftJustGenerated] = useState(false);
  // Drives the note-preview-style "voice edit applied" shine flash over the
  // composer. The epoch re-keys the overlay so each voice edit replays it.
  const [voiceEditFlashEpoch, setVoiceEditFlashEpoch] = useState(0);
  const voiceEditFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (voiceEditFlashTimerRef.current)
        clearTimeout(voiceEditFlashTimerRef.current);
    },
    [],
  );

  const triggerVoiceEditFlash = useCallback(() => {
    setVoiceEditFlashEpoch(epoch => epoch + 1);
    if (voiceEditFlashTimerRef.current)
      clearTimeout(voiceEditFlashTimerRef.current);
    voiceEditFlashTimerRef.current = setTimeout(() => {
      setVoiceEditFlashEpoch(0);
      voiceEditFlashTimerRef.current = null;
    }, VOICE_EDIT_FLASH_MS);
  }, []);

  const handleAdd = async () => {
    const text = draft.trim();
    if (!text) return;
    const result = await onAdd(text);
    // Keep the draft if the add explicitly failed so the user can retry.
    if (result !== false) setDraft('');
  };

  // Drop any existing "Opalite ID: …" trailer so a regenerate/merge never
  // stacks two id tags — the freshly generated text re-appends its own.
  const stripOpaliteTag = (value: string) =>
    value.replace(/\n*\s*opalite id:[\s\S]*$/i, '').trim();

  const handleGenerate = async () => {
    if (!onGenerate) return;
    setGenerating(true);
    try {
      const text = (await onGenerate())?.trim();
      if (text) {
        setDraft(prev => {
          const base = stripOpaliteTag(prev);
          return base ? `${base}\n${text}` : text;
        });
        setDraftJustGenerated(true);
      }
    } finally {
      setGenerating(false);
    }
  };

  // One-shot auto-draft from the call. Guarded so editing/clearing the composer
  // afterwards never re-triggers it.
  const autoGeneratedRef = useRef(false);
  useEffect(() => {
    if (!autoGenerate || !onGenerate) return;
    if (isLocked) return;
    if (actions.length > 0) return;
    if (draft.trim()) return;
    if (autoGeneratedRef.current) return;
    autoGeneratedRef.current = true;
    void handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, isLocked, actions.length]);

  const handleDictated = async (spokenText: string) => {
    const instruction = spokenText.trim();
    if (!instruction) return;
    // No backend voice edit wired — fall back to appending the raw transcript.
    if (!onVoiceEdit) {
      setDraft(prev => (prev.trim() ? `${prev} ${instruction}` : instruction));
      triggerVoiceEditFlash();
      return;
    }
    // Route the spoken instruction through the LLM so commands ("remove the
    // line about the telephone encounter", "make it more concise") are applied
    // to the draft instead of appended verbatim. On failure (caller returns
    // null) we keep the existing draft untouched.
    const revised = await onVoiceEdit(draftRef.current, instruction);
    if (revised != null) {
      setDraft(revised);
      triggerVoiceEditFlash();
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      {actions.length === 0 ? (
        <Text fontSize="md" color={subtleText}>
          No actions taken yet.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {actions.map(action => (
            <Box
              key={action.id}
              bg={panelBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              px={4}
              py={3}
            >
              <Flex
                align="baseline"
                justify="space-between"
                gap={3}
                mb={1}
                flexWrap="wrap"
              >
                <HStack spacing={2} align="baseline" minW={0}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={textColor}
                    noOfLines={1}
                  >
                    {action.authorName}
                  </Text>
                  {action.authorRole ? (
                    <Text fontSize="xs" color={subtleText} flexShrink={0}>
                      {action.authorRole}
                    </Text>
                  ) : null}
                </HStack>
                <Text
                  fontSize="xs"
                  color={subtleText}
                  flexShrink={0}
                  sx={{fontVariantNumeric: 'tabular-nums'}}
                >
                  {formatActionTime(action.createdAt)}
                </Text>
              </Flex>
              <Text
                fontSize="md"
                color={textColor}
                whiteSpace="pre-wrap"
                lineHeight="1.5"
              >
                {action.text}
              </Text>
            </Box>
          ))}
        </VStack>
      )}

      {isLocked ? (
        <HStack spacing={2} color={subtleText} fontSize="sm">
          <TablerLock size={14} aria-hidden="true" />
          <Text>This encounter is closed. The thread is locked.</Text>
        </HStack>
      ) : (
        <Box>
          <Box position="relative">
            <Textarea
              ref={composerRef}
              value={draft}
              onChange={e => {
                setDraftJustGenerated(false);
                setDraft(e.target.value);
              }}
              onAnimationEnd={() => setDraftJustGenerated(false)}
              sx={draftJustGenerated ? TE_GENERATED_REVEAL_SX : undefined}
              placeholder="Add an action taken — e.g. spoke to patient, gave instructions, acknowledged understanding."
              rows={3}
              maxH={`${COMPOSER_MAX_HEIGHT_PX}px`}
              borderRadius="lg"
              borderColor={borderColor}
              color={textColor}
              fontSize="md"
              resize="vertical"
              _hover={{borderColor}}
              _focusVisible={inputFocusStyles}
            />
            {voiceEditFlashEpoch > 0 ? (
              <DictationEditFlash
                key={`te-action-flash-${voiceEditFlashEpoch}`}
                borderRadius="lg"
              />
            ) : null}
          </Box>
          <Flex
            justify="flex-end"
            mt={3}
            gap={3}
            flexWrap="wrap"
            align="center"
          >
            <Box mr="auto">
              <TeDictationButton onInsert={handleDictated} label="Voice edit" />
            </Box>
            {onGenerate ? (
              <Button
                variant="outline"
                size="md"
                fontSize="sm"
                leftIcon={<TablerBolt size={16} aria-hidden="true" />}
                isLoading={generating}
                loadingText="Generating"
                onClick={handleGenerate}
              >
                Generate from call
              </Button>
            ) : null}
            <Button
              {...RECAP_PRIMARY_BUTTON_PROPS}
              size="md"
              fontSize="sm"
              leftIcon={<TablerPlus size={16} aria-hidden="true" />}
              isLoading={isAdding}
              loadingText="Adding"
              isDisabled={!draft.trim()}
              onClick={handleAdd}
            >
              Add action taken
            </Button>
          </Flex>
        </Box>
      )}
    </VStack>
  );
};

export default ActionTakenThread;
